import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    input,
    InputSignal,
    InputSignalWithTransform
} from '@angular/core';
import { Spinner } from '../spinner/spinner';
import { MiniButtonSize } from './mini-button-size';

/**
 * The `<app-mini-button>` component is an icon-only button. It is to be used in dense UIs such as tables, toasts, and inputs.
 *
 * @example
 * ```HTML
 * <button app-mini-button size="default">
 *     <app-icon type="download"/>
 * </button>
 * ```
 */
@Component({
    /* eslint-disable-next-line @angular-eslint/component-selector */
    selector: 'button[app-mini-button]',
    templateUrl: './mini-button.html',
    styleUrl: './mini-button.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'class': 'app-mini-button',
        '[attr.aria-busy]': 'loading()',
        '[attr.aria-disabled]': 'disabled() || loading()',
        '[attr.aria-label]': 'label()',
        '[class.app-mini-button--size-default]': 'size() === "default"',
        '[class.app-mini-button--size-small]': 'size() === "small"',
        '[disabled]': 'disabled() || loading()',
        '[tabindex]': '(disabled() || loading()) ? -1 : 0',
    },
    imports: [
        Spinner,
    ],
})
export class MiniButton {
    /**
     * Whether the mini button is disabled. Defaults to `false`.
     */
    public readonly disabled: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(
        false,
        {
            transform: booleanAttribute,
        },
    );

    /**
     * Whether the mini button should be shown as loading. Defaults to `false`.
     */
    public readonly loading: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(
        false,
        {
            transform: booleanAttribute,
        },
    );

    /**
     * The label of the mini button.
     */
    public readonly label: InputSignal<string> = input.required<string>();

    /**
     * The size of the mini button. Defaults to `default`.
     */
    public readonly size: InputSignal<MiniButtonSize> = input<MiniButtonSize>('default');
}